/**
 * Einstiegspunkt: Styles laden, Theme setzen, Router starten,
 * Service Worker registrieren.
 */
import './styles/index.css';
import './styles/depth.css';
import { startRouter } from './app.ts';
import { getProject, getSettings, listProjects, saveSettings } from './db/repo.ts';
import { migrateNotesToBible } from './db/migrate-notes.ts';
import { applyTheme, watchSystemTheme } from './theme/themes.ts';
import { setAppearance } from './theme/appearance.ts';
import { applyUiScale } from './theme/ui-scale.ts';
import { mountToolMenu } from './features/drawing/menu.ts';
import { watchPencilLongPress } from './features/drawing/longpress.ts';
import { mountInstallHint } from './features/install-hint.ts';
import { h } from './ui/dom.ts';
import { firebaseEnabled } from './firebase/config.ts';
import { installErrorLog } from './features/error-log.ts';
import { installErrorSnake, showErrorSnake } from './features/error-snake.ts';
import { watchTypedSnake } from './features/snake.ts';
import { setBootStep, watchBoot } from './features/boot-watch.ts';
import { appUrl } from './platform/endpoints.ts';
import { registerServiceWorker as registerSw } from './pwa/register.ts';

async function boot(): Promise<void> {
  // Die Verwaltung liegt in System (eigene Seite): alte Links auf #/admin
  // sofort weiterleiten – ohne erst Lead Sheets anzumelden und zu laden.
  const admin = /^#\/admin(?:\/([\w-]+))?$/.exec(location.hash);
  if (admin) {
    location.replace(appUrl('system', admin[1] ? `#/${admin[1]}` : ''));
    return;
  }

  // Fehler fuer die Diagnose im Admin-Bereich mitschreiben (nur im Geraet)
  // und bei echten Fehlern Snake mit dem Weg zurueck zeigen.
  installErrorLog();
  installErrorSnake();
  watchTypedSnake();

  /*
   * Anmeldung und Cloud nur, wenn in .env ein Firebase-Projekt steht.
   * Sonst laeuft die App wie immer: ohne Anmeldung, alles im Geraet.
   * Nachgeladen statt fest eingebunden, damit die lokale Fassung den
   * Firebase-Code gar nicht erst mitschleppt.
   */
  if (firebaseEnabled) {
    registerServiceWorker();
    const root = document.getElementById('app');
    if (root) {
      root.append(h('p', { class: 'login__loading', text: 'Lade …' }));
      watchBoot(root);
    }
    setBootStep('Anmeldung laden');
    const { startCloud } = await import('./firebase/start.ts');
    await startCloud(root ?? document.body, { offlineCache: true, area: 'leadSheets' });
  }

  // Theme vor dem ersten Rendern setzen, damit nichts umspringt.
  let themeId = 'paper';
  try {
    const settings = await getSettings();
    themeId = settings.themeId;
    setAppearance({ ...settings, customImage: settings.customBackground ?? null });
    applyUiScale(settings.uiScale);
    applyTheme(themeId);
  } catch (err) {
    console.error('Einstellungen konnten nicht geladen werden', err);
  }

  // Im Modus "Automatisch" mit dem Geraet mitwechseln.
  watchSystemTheme(() => themeId);
  window.addEventListener('leadsheet:theme', (ev) => {
    themeId = (ev as CustomEvent<string>).detail;
  });

  /*
   * Alte Notizen in die Band-Bibel ueberfuehren.
   *
   * Der Reiter "Notizen" ist weg; die Daten duerfen deshalb nicht liegen
   * bleiben, wo niemand mehr hinkommt. Laeuft bei jedem Start, macht aber
   * nur etwas, wenn tatsaechlich noch Notizen da sind – auch solche aus
   * einem spaeter eingespielten alten Backup.
   */
  try {
    for (const project of await listProjects()) {
      const { moved } = await migrateNotesToBible(project.id);
      if (moved > 0) {
        console.info(`${moved} Notizen nach Band-Bibel > Allgemein überführt.`);
      }
    }
  } catch (err) {
    // Schlaegt das fehl, soll die App trotzdem starten.
    console.error('Notizen konnten nicht überführt werden', err);
  }

  // Jeder Start beginnt im Regal, egal wo die App zuletzt stand – ausser
  // bei gezielten Spruengen aus Schnittstelle Rot: Admin, Einstellungen oder
  // ein bestimmtes Projekt (#/open/<id>).
  history.replaceState(null, '', await startHash(location.hash.replace(/^#/, '')));
  startRouter();

  // Werkzeugpalette fuer den Pencil – einmal fuer die ganze App.
  const palette = mountToolMenu();

  // Ersatz fuer das Druecken am Apple Pencil Pro, das es im Browser nicht gibt.
  watchPencilLongPress((x, y) => palette.open(x, y));

  // Einmaliger Hinweis, wie die App auf den Home-Bildschirm kommt.
  mountInstallHint();

  // Mit Anmeldung ist er schon oben registriert, damit auch der
  // Anmeldebildschirm offline startet.
  //
  // Bewusst ohne "load"-Listener: boot() laeuft asynchron und ist oft erst
  // nach dem load-Ereignis fertig; der Listener wuerde dann nie ausloesen
  // und die App waere nicht offline verfuegbar.
  if (!firebaseEnabled) registerServiceWorker();
}

/** Wohin der Start fuehrt. Unbekanntes landet im Regal. */
async function startHash(requested: string): Promise<string> {
  if (/^\/settings(\/[\w-]+)*$/.test(requested)) return `#${requested}`;
  const open = /^\/open\/([\w-]{1,80})$/.exec(requested);
  if (open) {
    try {
      const project = await getProject(open[1] as string);
      if (project) {
        await saveSettings({ ...(await getSettings()), activeProjectId: project.id });
        return '#/';
      }
    } catch (err) {
      console.error('Projekt konnte nicht geöffnet werden', err);
    }
  }
  return '#/projects';
}

/** Service Worker und automatische Updates (pwa/register.ts). */
function registerServiceWorker(): void {
  registerSw('./sw.js');
}

boot().catch((err: unknown) => {
  console.error(err);
  showErrorSnake(err instanceof Error ? err.message : String(err));
});
